/**
 * Internationalization — English / Russian translations
 * Usage: const t = useTranslations(lang)  →  t("key")
 */

export type Lang = "en" | "ru"
export const LANGUAGES: { id: Lang; label: string }[] = [
  { id: "en", label: "EN" },
  { id: "ru", label: "RU" },
]

type Translations = Record<string, string>

const en: Translations = {
  // ── App chrome ──
  "app.title": "Finance Analytics",
  "app.connected": "Connected",
  "app.loading": "Loading financial data...",
  "app.loadingSub": "Connecting to fact tables",

  // ── Auth ──
  "auth.title": "Finance Analytics",
  "auth.subtitle": "Sign in to continue",
  "auth.email": "Email",
  "auth.password": "Password",
  "auth.signIn": "Sign in",
  "auth.invalid": "Invalid credentials",
  "auth.demo": "Demo accounts",

  // ── Top bar ──
  "topbar.templates": "Templates",
  "topbar.export": "Export",
  "topbar.panelCsv": "Panel as CSV",
  "topbar.allCsv": "All panels CSV",
  "topbar.exportPdf": "Export PDF with charts",
  "topbar.csv": "CSV",

  // ── User menu ──
  "user.faq": "FAQ",
  "user.admin": "User management",
  "user.signOut": "Sign out",

  // ── Tabs ──
  "tab.dashboard": "Dashboard",
  "tab.newWorkspace": "New workspace",
  "tab.newAnalysis": "New analysis",
  "tab.analysis": "Analysis",
  "tab.openInWorkspace": "Open in workspace",

  // ── Sidebar ──
  "sidebar.config": "Configuration",
  "sidebar.wsConfig": "Workspace config",
  "sidebar.viewOnly": "View only",
  "sidebar.viewOnlyMsg": "View-only access",
  "sidebar.contactAdmin": "Contact admin for edit permissions",
  "sidebar.valueItems": "Value items",
  "sidebar.splitItems": "Split items",
  "sidebar.addDimensions": "Add dimensions",
  "sidebar.addDimHelp": "Click + to add, then configure under the chart",
  "sidebar.searchDims": "Search dimensions...",
  "sidebar.quickFind": "Quick find...",
  "sidebar.items": "items",
  "sidebar.more": "more…",

  // ── Chart ──
  "chart.noData": "No data",
  "chart.noDataConfig": "No data — configure panel",
  "chart.total": "Total",

  // ── Chart types ──
  "chartType.bar": "Bar",
  "chartType.stackedBar": "Stacked column",
  "chartType.hbar": "Horizontal bar",
  "chartType.hbarStacked": "Stacked bar",
  "chartType.line": "Line",
  "chartType.area": "Area",
  "chartType.pie": "Pie",
  "chartType.table": "Table",

  // ── Sort ──
  "sort.title": "Sort chart data",
  "sort.default": "Default order",
  "sort.desc": "Value high → low",
  "sort.asc": "Value low → high",
  "sort.alphaAsc": "A → Z",
  "sort.alphaDesc": "Z → A",

  // ── Y-Axis ──
  "yaxis.title": "Y-Axis Settings",
  "yaxis.tooltip": "Y-axis",
  "yaxis.min": "Min",
  "yaxis.max": "Max",
  "yaxis.tickInterval": "Tick interval",
  "yaxis.magnitude": "Magnitude",
  "yaxis.decimals": "Decimal places",
  "yaxis.reset": "Reset to auto",
  "yaxis.auto": "Auto",
  "yaxis.ones": "Ones (1,234)",
  "yaxis.thousands": "Thousands (1.2K)",
  "yaxis.millions": "Millions (1.2M)",
  "yaxis.billions": "Billions (1.2B)",

  // ── Drop zones ──
  "zone.xaxis": "X-axis",
  "zone.filter": "Filter",
  "zone.split": "Split",
  "zone.drop": "drop",
  "zone.value": "V",
  "zone.remove": "Remove",
  "zone.incompatible": "\"{dim}\" does not apply to {sources} — those rows all group under one placeholder",

  // ── Chart density warnings ──
  "warn.manySeries": "{n} series — only {max} distinct colours, so some repeat. Split by fewer values.",
  "warn.manyCategories": "{n} categories on the x-axis — hard to read. Filter down or use a table.",

  // ── Data integrity warnings ──
  "warn.planActual": "Plan and actual figures are being added together. Filter by Scenario, or move Data source to the Split shelf.",
  "warn.payrollDouble": "Payroll counted twice — it is inside both Costs and Payroll detail. Keep one of them.",
  "warn.netDouble": "Net P/L is revenue minus costs, so adding it to Revenue or Costs counts the same money twice.",

  // ── Floating editor ──
  "editor.role": "Role",
  "editor.search": "Search...",
  "editor.all": "All",
  "editor.clear": "Clear",
  "editor.invert": "Invert",
  "editor.selectAll": "Select all",
  "editor.deselectAll": "Deselect all",

  // ── Templates ──
  "tpl.saveTitle": "Save as template",
  "tpl.saveDesc": "Give this chart configuration a name so you can reuse it later.",
  "tpl.name": "Template name",
  "tpl.placeholder": "e.g. Revenue by Client Q4",
  "tpl.chartType": "Chart type:",
  "tpl.values": "Values:",
  "tpl.dimensions": "Dimensions:",
  "tpl.active": "active",
  "tpl.cancel": "Cancel",
  "tpl.save": "Save template",
  "tpl.saveTooltip": "Save template",
  "tpl.browseTitle": "Saved templates",
  "tpl.browseDesc": "Click a template to open it in a new workspace tab.",
  "tpl.empty": "No templates saved yet.",
  "tpl.emptyHint": "Click the save icon on any chart to save it as a template.",
  "tpl.saved": "Saved",
  "tpl.dims": "dims",

  // ── Admin ──
  "admin.title": "User management",
  "admin.desc": "Manage user roles, entity access, and permissions",
  "admin.user": "User",
  "admin.role": "Role",
  "admin.entities": "Entities",
  "admin.years": "Years",
  "admin.access": "Access",
  "admin.actions": "Actions",
  "admin.edit": "Edit",
  "admin.save": "Save",
  "admin.cancel": "Cancel",
  "admin.roleAdmin": "Admin",
  "admin.roleEditor": "Editor",
  "admin.roleViewer": "Viewer",
  "admin.accessView": "View",
  "admin.accessEdit": "Edit",

  // ── FAQ ──
  "faq.title": "FAQ",
  "faq.desc": "Common questions about the platform",
  "faq.q1": "How do I configure a chart panel?",
  "faq.a1": "Click any panel to select it (blue outline). The right sidebar shows that panel's configuration. Use 'Value items' to select metrics and 'Split items' to set dimensions.",
  "faq.q2": "What's the difference between X-axis, Filter, and Split?",
  "faq.a2": "X-axis controls what appears along the bottom of the chart. Filter restricts the data shown. Split creates separate series (e.g., each company as its own line).",
  "faq.q3": "How do I change a dimension's role?",
  "faq.a3": "Click the dimension's pill in the strip beside the chart. The editor that opens has a Role dropdown — switch between X-axis, Filter, and Split there, or just drag the pill to another shelf.",
  "faq.q4": "How do I see raw data?",
  "faq.a4": "Use the Table icon (last in the chart type selector) to switch any panel to a data table view.",
  "faq.q5": "How do I export?",
  "faq.a5": "Click Export in the top bar. Options: single panel CSV, all panels CSV, or PDF report with chart screenshots.",
  "faq.q6": "What are workspaces?",
  "faq.a6": "Click '+ New workspace' in the tab bar to create a full-size single chart/table workspace for deep analysis. Each workspace is fully independent.",
  "faq.q7": "Why can't I edit?",
  "faq.a7": "Your account has view-only access. Contact your administrator to upgrade permissions.",
  "faq.q8": "How is my data filtered?",
  "faq.a8": "Your view is automatically filtered based on your permissions — you only see entities and years your admin has granted access to.",

  // ── Select role descriptions ──
  "role.xaxisDesc": "Horizontal axis",
  "role.filterDesc": "Restrict data",
  "role.splitDesc": "Separate series",

  // ── PDF export ──
  "pdf.title": "Financial Analytics Report",
  "pdf.generated": "Generated",
  "pdf.at": "at",
  "pdf.captureError": "Could not capture chart image",

  // ── Value tree labels ──
  "val.cashflow": "Cash Flow",
  "val.assets": "Assets Held",
  "val.transactionvolume": "Transaction Volume",
  "val.balance": "Balance",
  "val.feerevenue": "Fee Revenue",

  // ── Dimension group labels ──
  "dim.timeline": "Timeline",
  "dim.classification": "Classification",
  "dim.categories": "Categories",
  "dim.counterparty": "Counterparty",
  "dim.other": "Other",

  // ── Dimension labels ──
  "dim.year": "Year",
  "dim.quarter": "Quarter",
  "dim.month": "Month",
  "dim.dataSource": "Data source",
  "dim.costType": "Cost type",
  "dim.counterpartyType": "Counterparty type",
  "dim.account": "Category",
  "dim.costCategory": "Cost category",
  "dim.revenueGroup": "Revenue group",
  "dim.assetClass": "Asset class",
  "dim.counterpartyName": "Counterparty",
  "dim.clientName": "Client",
  "dim.entity": "Entity",
  "dim.currency": "Currency",
  "dim.contraCategory": "Contra category",
  "dim.serviceType": "Service type",
  "data.AUM": "Portfolio Management",
  "data.AUC": "Safekeeping",
  "data.IA": "Investment Advice",
  "data.OWN": "Own Book",

  // Data-source codes. These are row keys referenced by saved templates and
  // preset filters, so they stay as-is in the data and are only renamed here,
  // for display. "(plan)" is part of the name deliberately — those three are
  // the fin model forecast, not booked actuals.
  "data.costs": "Costs",
  "data.revenue": "Revenue",
  "data.net": "Net P/L",
  "data.positions": "Client positions",
  "data.transactions": "Transactions",
  "data.balances": "Balances",
  "data.fees": "Fees",
  "data.jira_invoices": "Supplier invoices",
  "data.payroll_actual": "Payroll detail",
  "data.budget_expense": "Expenses (plan)",
  "data.budget_payroll": "Payroll (plan)",
  "data.pl_plan": "P&L (plan)",

  // ── Default panel titles ──
  "panel.revenueByClient": "Revenue by Client (2025)",
  "panel.aumTrend": "AUM / AUC (monthly trend)",
  "panel.revenueVsExpenses": "Revenue vs Expenses (yearly)",
  "panel.expensesByCategory": "Expenses by Category (yearly)",

  // ── Template folders ──
  "folder.revenue_pl": "Revenue & P&L",
  "folder.assets_aum": "Assets & AUM",
  "folder.clients": "Clients",
  "folder.costs": "Cost Analysis",
  "folder.custom": "Custom",
  "folder.label": "Folders",
  "folder.folderLabel": "Folder",

  // ── Template browser ──
  "tpl.browseDescFull": "Browse templates by category or load a saved configuration into a new workspace.",
  "tpl.noCustom": "No custom templates yet",
  "tpl.noTemplates": "No templates in this folder",
  "tpl.noCustomHint": "Save a chart configuration to create one",
  "tpl.builtin": "Built-in",

  // ── Panel picker ──
  "picker.title": "Replace dashboard panel",
  "picker.desc": "pick which panel to replace:",
  "picker.loading": "Loading",
  "picker.slot": "Slot",

  // ── Navigation ──
  "nav.portal": "Portal",
  "nav.backToPortal": "Back to Portal",

  // ── Group view toggle ──
  // The workbench is sliced by group, not by legal entity: a single company
  // is reached by putting Entity on a shelf inside one of these views.
  "entity.all": "All",
  "view.residualTitle": "Not in either group",
  "view.residualBody": "{rows} rows ({value}) are booked to a payer nobody has assigned to a company yet, so they appear only under All. WWG and The Vault therefore do not add up to All.",
  "view.residualFix": "Assign them in Records → Entity Names.",
  "view.residualExternal": "A further {rows} rows ({value}) are marked as external and are excluded on purpose.",

  // ── KPI cards ──
  "kpi.totalRevenue": "Total Revenue",
  "kpi.totalExpenses": "Total Expenses",
  "kpi.operatingMargin": "Operating Margin",
  "kpi.aumAuc": "AUM / AUC",
  "kpi.revenueMinusCosts": "Revenue minus costs",
  "kpi.activeClients": "Active Clients",
  "kpi.activeClientsSub": "active clients",
  "kpi.vs": "vs",
  "kpi.in": "in",

  // ── Default panel titles (WWDA) ──
  "panel.txVolByType": "Transaction Volume by Type",
  "panel.monthlyTxTrend": "Monthly Transaction Trend",
  "panel.topClientsByVol": "Top Clients by Volume",
  "panel.feeRevenueYearly": "Fee Revenue (yearly)",
  "panel.balanceMonthlyTrend": "Balance Monthly Trend",
  "panel.feeRevByCurrency": "Fee Revenue by Currency",

  // ── Default panel titles (Unified) ──
  "panel.revByEntityYearly": "Revenue by Entity (yearly)",
  "panel.topClientsAll": "Top Clients (all entities)",
  "panel.monthlyTrendAll": "Monthly Trend (all entities)",
  "panel.dataSourceBreakdown": "Data Source Breakdown",

  // ── Default panel titles (WWF) ──
  "panel.revVsExpenses": "Revenue vs Expenses",
  "panel.revByClient": "Revenue by Client",
  "panel.aumServiceMonthly": "AUM by Service Type (monthly)",
  "panel.costStructYearly": "Cost Structure (yearly)",

  // ── Builtin template names ──
  "panel.expRevByCategory": "Expenses & Revenue by Category",
  "panel.clientRevVsCosts": "Client Revenue vs Costs",

  // ── 2026-04 pre-set rework (CFO spec) ──
  "panel.cashFlowWithNet": "Cash Flow — Revenue, Costs & Net P/L",
  "panel.aumByClient": "AUM by Client (YTD)",
  "panel.revByMonthByClient": "Revenue by Month by Client (YTD)",
  "panel.costStructMonthly": "Cost Structure — Monthly",
  "panel.revByCounterpartyMonthly": "Revenue by Month by Counterparty (YTD)",
  "panel.revByCategoryMonthly": "Revenue by Service Category — Monthly",
  "panel.treasuryBalanceMonthly": "Treasury Balance by Asset Class",
  "panel.feeRevMonthlyTrend": "Fee Revenue — Monthly Trend",
  "panel.walletBalanceByClient": "Wallet Balance by Client (YTD)",
  "panel.feeRevByMonthByClient": "Fee Revenue by Month by Client (YTD)",
  "panel.feeStructMonthly": "Fee Structure — Monthly",

  // ── Dashboard presets ──
  "preset.menuLabel": "Pre-built dashboards",
  "preset.default": "Default",
  "preset.yearInReview": "Year in Review",
  "preset.topClients": "Top 10 Clients",
  "preset.payroll": "Payroll Analytics",
  "preset.costControl": "Cost Control & AP",
  "preset.byCompany": "By Company",
  "preset.planActual": "Plan vs Actual",
  "preset.wallet": "Wallet & Transactions",
  "preset.topClientsFees": "Top 10 Clients",

  // ── Preset panel titles ──
  "panel.p.revCostNetMonth": "Revenue · Costs · Net — by Month (YTD)",
  "panel.p.netByQuarter": "Net Cash Flow by Quarter (YoY)",
  "panel.p.costStructYtd": "Cost Structure by Month (YTD)",
  "panel.p.revByCategory": "Revenue by Category (YTD)",
  "panel.p.top10ClientsRev": "Top 10 Clients by Revenue (YTD)",
  "panel.p.top10ClientsTrend": "Top 10 Clients — Monthly Trend (YTD)",
  "panel.p.clientConcentration": "Client Concentration (YTD)",
  "panel.p.top10RevVsCost": "Top 10 Clients — Revenue vs Costs (YTD)",
  "panel.p.payrollMonthYoY": "Payroll Cost by Month — Actual (YoY)",
  "panel.p.payrollByDivision": "Payroll by Division — Plan (YTD)",
  "panel.p.payrollByTeam": "Payroll by Team — Plan (YTD)",
  "panel.p.payrollByPosition": "Payroll by Position — Plan (YTD)",
  "panel.p.topVendors": "Top 15 Vendors by Spend (YTD)",
  "panel.p.directVsOwn": "Direct vs Own Expense — by Month (YTD)",
  "panel.p.apPipeline": "Invoice Approval Pipeline (YTD)",
  "panel.p.topCostCategories": "Top Cost Categories (YTD)",
  "panel.p.revCostByEntity": "Revenue & Costs by Company (YTD)",
  "panel.p.netByEntity": "Net P/L by Company (YTD)",
  "panel.p.revByEntityMonth": "Revenue by Company — by Month (YTD)",
  "panel.p.payrollByEntity": "Payroll by Company (YTD)",
  "panel.p.payrollEntityMonth": "Payroll by Company — by Month (YTD)",
  "panel.p.byJurisdiction": "Revenue & Costs by Jurisdiction (YTD)",
  "panel.p.costByEntity": "Costs by Company (YTD)",
  "panel.p.spendByEntity": "Supplier Spend by Company (YTD)",
  "panel.p.planVsActualMonth": "Plan vs Actual — by Month (YTD)",
  "panel.p.planVsActualEntity": "Plan vs Actual — by Company (YTD)",
  "panel.p.planVsActualQuarter": "Plan vs Actual — by Quarter (YTD)",
  "panel.p.top10ClientsFees": "Top 10 Clients by Fees (YTD)",
  "panel.p.top10FeesTrend": "Top 10 Clients — Fees by Month (YTD)",
  "panel.p.txVolumeMonth": "Transaction Volume by Month (YoY)",
  "panel.p.txByCategory": "Transaction Volume by Operation (YTD)",
  "panel.p.feeByType": "Fee Revenue by Type — by Month (YTD)",
  "panel.p.walletBalanceProduct": "Wallet Balances by Product (YTD)",
  "panel.p.totalByMonth": "Total by Month (YTD)",

  // ── NL Query ──
  "nl.placeholder": "Ask anything... \"revenue by client for 2025\"",
  "nl.query": "Query",
  "nl.chartCreated": "Chart created",
  "nl.tryAsking": "Try asking",
  "nl.metric": "Metric",
  "nl.metricDefault": "(default)",
  "nl.xaxis": "X-axis",
  "nl.split": "Split",
  "nl.chart": "Chart",
  "nl.yearFilter": "Year filter",
  "nl.year": "Year",
  "nl.latest": "(latest)",
  "nl.sortDesc": "Sort: descending (top)",
  "nl.sortAsc": "Sort: ascending (bottom)",
  "nl.ex1": "revenue by client for 2025",
  "nl.ex2": "top expenses by category this year",
  "nl.ex3": "monthly revenue trend split by year",
  "nl.ex4": "show me costs vs revenue yearly as stacked bar",
  "nl.ex5": "fee revenue by month as area chart",
  "nl.ex6": "client concentration — who are the biggest?",
  "nl.ex7": "transaction volume by quarter",
  "nl.ex8": "aum by currency as pie chart",
  "nl.ex9": "compare revenue across entities as a table",

  // ── KPI (WWDA/ALL) ──
  "kpi.totalVolume": "Total Volume",
  "kpi.transactionVolume": "Transaction Volume",
  "kpi.feeRevenue": "Fee Revenue",
  "kpi.aumBalance": "AUM / Balance",
  "kpi.vsLastYear": "vs {value} last year",
  "kpi.unique": "unique",
  "kpi.lastYear": "last year",

  // ── Misc ──
  "misc.cancel": "Cancel",
  "misc.dims": "dims",
}

const ru: Translations = {
  // ── App chrome ──
  "app.title": "Финансовая Аналитика",
  "app.connected": "Подключено",
  "app.loading": "Загрузка финансовых данных...",
  "app.loadingSub": "Подключение к таблицам",

  // ── Auth ──
  "auth.title": "Финансовая Аналитика",
  "auth.subtitle": "Войдите для продолжения",
  "auth.email": "Электронная почта",
  "auth.password": "Пароль",
  "auth.signIn": "Войти",
  "auth.invalid": "Неверные учетные данные",
  "auth.demo": "Демо-аккаунты",

  // ── Top bar ──
  "topbar.templates": "Шаблоны",
  "topbar.export": "Экспорт",
  "topbar.panelCsv": "Панель как CSV",
  "topbar.allCsv": "Все панели CSV",
  "topbar.exportPdf": "Экспорт PDF с графиками",
  "topbar.csv": "CSV",

  // ── User menu ──
  "user.faq": "ЧаВо",
  "user.admin": "Управление пользователями",
  "user.signOut": "Выйти",

  // ── Tabs ──
  "tab.dashboard": "Панель",
  "tab.newWorkspace": "Новое рабочее пространство",
  "tab.newAnalysis": "Новый анализ",
  "tab.analysis": "Анализ",
  "tab.openInWorkspace": "Открыть в рабочем пространстве",

  // ── Sidebar ──
  "sidebar.config": "Настройки",
  "sidebar.wsConfig": "Настройки пространства",
  "sidebar.viewOnly": "Только просмотр",
  "sidebar.viewOnlyMsg": "Доступ только для просмотра",
  "sidebar.contactAdmin": "Обратитесь к администратору для получения прав",
  "sidebar.valueItems": "Показатели",
  "sidebar.splitItems": "Измерения",
  "sidebar.addDimensions": "Добавить измерения",
  "sidebar.addDimHelp": "Нажмите +, затем настройте под графиком",
  "sidebar.searchDims": "Поиск измерений...",
  "sidebar.quickFind": "Быстрый поиск...",
  "sidebar.items": "элементов",
  "sidebar.more": "ещё…",

  // ── Chart ──
  "chart.noData": "Нет данных",
  "chart.noDataConfig": "Нет данных — настройте панель",
  "chart.total": "Итого",

  // ── Chart types ──
  "chartType.bar": "Столбчатая",
  "chartType.stackedBar": "Столбчатая с накоплением",
  "chartType.hbar": "Горизонтальная",
  "chartType.hbarStacked": "Горизонтальная с накоплением",
  "chartType.line": "Линейная",
  "chartType.area": "Область",
  "chartType.pie": "Круговая",
  "chartType.table": "Таблица",

  // ── Sort ──
  "sort.title": "Сортировка данных",
  "sort.default": "По умолчанию",
  "sort.desc": "По убыванию",
  "sort.asc": "По возрастанию",
  "sort.alphaAsc": "А → Я",
  "sort.alphaDesc": "Я → А",

  // ── Y-Axis ──
  "yaxis.title": "Настройки оси Y",
  "yaxis.tooltip": "Ось Y",
  "yaxis.min": "Мин",
  "yaxis.max": "Макс",
  "yaxis.tickInterval": "Шаг делений",
  "yaxis.magnitude": "Масштаб",
  "yaxis.decimals": "Десятичные знаки",
  "yaxis.reset": "Сбросить",
  "yaxis.auto": "Авто",
  "yaxis.ones": "Единицы (1 234)",
  "yaxis.thousands": "Тысячи (1,2К)",
  "yaxis.millions": "Миллионы (1,2М)",
  "yaxis.billions": "Миллиарды (1,2Б)",

  // ── Drop zones ──
  "zone.xaxis": "Ось X",
  "zone.filter": "Фильтр",
  "zone.split": "Разрез",
  "zone.drop": "перетащите",
  "zone.value": "П",
  "zone.remove": "Убрать",
  "zone.incompatible": "«{dim}» не применяется к {sources} — эти строки попадут в одну заглушку",

  // ── Chart density warnings ──
  "warn.manySeries": "{n} рядов — всего {max} различных цветов, часть повторяется. Уменьшите разрез.",
  "warn.manyCategories": "{n} категорий по оси X — читать сложно. Отфильтруйте или используйте таблицу.",

  // ── Data integrity warnings ──
  "warn.planActual": "План и факт суммируются вместе. Отфильтруйте по сценарию или перенесите «Источник данных» в разрез.",
  "warn.payrollDouble": "ФОТ учтён дважды — он входит и в «Затраты», и в «ФОТ — детализация». Оставьте один источник.",
  "warn.netDouble": "Чистая прибыль — это выручка минус затраты, поэтому её сложение с ними удваивает те же суммы.",

  // ── Floating editor ──
  "editor.role": "Роль",
  "editor.search": "Поиск...",
  "editor.all": "Все",
  "editor.clear": "Очистить",
  "editor.invert": "Инвертировать",
  "editor.selectAll": "Выбрать все",
  "editor.deselectAll": "Снять выбор",

  // ── Templates ──
  "tpl.saveTitle": "Сохранить как шаблон",
  "tpl.saveDesc": "Дайте этой конфигурации имя для повторного использования.",
  "tpl.name": "Название шаблона",
  "tpl.placeholder": "напр. Выручка по клиентам Q4",
  "tpl.chartType": "Тип графика:",
  "tpl.values": "Показатели:",
  "tpl.dimensions": "Измерения:",
  "tpl.active": "активно",
  "tpl.cancel": "Отмена",
  "tpl.save": "Сохранить",
  "tpl.saveTooltip": "Сохранить шаблон",
  "tpl.browseTitle": "Сохранённые шаблоны",
  "tpl.browseDesc": "Нажмите на шаблон, чтобы открыть в новой вкладке.",
  "tpl.empty": "Шаблоны не сохранены.",
  "tpl.emptyHint": "Нажмите иконку сохранения на любом графике.",
  "tpl.saved": "Сохранён",
  "tpl.dims": "изм.",

  // ── Admin ──
  "admin.title": "Управление пользователями",
  "admin.desc": "Настройка ролей, доступа и разрешений",
  "admin.user": "Пользователь",
  "admin.role": "Роль",
  "admin.entities": "Юр. лица",
  "admin.years": "Годы",
  "admin.access": "Доступ",
  "admin.actions": "Действия",
  "admin.edit": "Изменить",
  "admin.save": "Сохранить",
  "admin.cancel": "Отмена",
  "admin.roleAdmin": "Админ",
  "admin.roleEditor": "Редактор",
  "admin.roleViewer": "Читатель",
  "admin.accessView": "Просмотр",
  "admin.accessEdit": "Редактирование",

  // ── FAQ ──
  "faq.title": "FAQ",
  "faq.desc": "Часто задаваемые вопросы о платформе",
  "faq.q1": "Как настроить панель графика?",
  "faq.a1": "Нажмите на любую панель (синяя рамка). Боковая панель справа покажет настройки. Используйте «Показатели» для выбора метрик и «Измерения» для настройки осей.",
  "faq.q2": "В чём разница между осью X, фильтром и разрезом?",
  "faq.a2": "Ось X определяет, что отображается внизу графика. Фильтр ограничивает данные. Разрез создаёт отдельные серии (например, каждая компания своей линией).",
  "faq.q3": "Как изменить роль измерения?",
  "faq.a3": "Нажмите на плашку измерения в полосе рядом с графиком. В открывшемся редакторе есть список «Роль» — переключите ось X, фильтр или разрез, либо просто перетащите плашку в другую зону.",
  "faq.q4": "Как посмотреть исходные данные?",
  "faq.a4": "Используйте иконку таблицы (последняя в выборе типа графика), чтобы переключить панель в режим таблицы.",
  "faq.q5": "Как экспортировать данные?",
  "faq.a5": "Нажмите Экспорт в верхней панели. Варианты: панель как CSV, все панели CSV или PDF-отчёт со скриншотами.",
  "faq.q6": "Что такое рабочие пространства?",
  "faq.a6": "Нажмите «+ Новое пространство» для создания полноэкранного графика/таблицы для глубокого анализа. Каждое пространство независимо.",
  "faq.q7": "Почему я не могу редактировать?",
  "faq.a7": "Ваш аккаунт имеет доступ только для просмотра. Обратитесь к администратору для расширения прав.",
  "faq.q8": "Как фильтруются мои данные?",
  "faq.a8": "Ваш обзор автоматически фильтруется на основе разрешений — вы видите только те юр. лица и годы, к которым вам предоставлен доступ.",

  // ── Select role descriptions ──
  "role.xaxisDesc": "Горизонтальная ось",
  "role.filterDesc": "Ограничить данные",
  "role.splitDesc": "Разделить серии",

  // ── PDF export ──
  "pdf.title": "Отчёт финансовой аналитики",
  "pdf.generated": "Сформирован",
  "pdf.at": "в",
  "pdf.captureError": "Не удалось захватить изображение",

  // ── Value tree labels ──
  "val.cashflow": "Денежный поток",
  "val.assets": "Активы",
  "val.transactionvolume": "Объём транзакций",
  "val.balance": "Баланс",
  "val.feerevenue": "Комиссионный доход",

  // ── Dimension group labels ──
  "dim.timeline": "Временная шкала",
  "dim.classification": "Классификация",
  "dim.categories": "Категории",
  "dim.counterparty": "Контрагент",
  "dim.other": "Прочее",

  // ── Dimension labels ──
  "dim.year": "Год",
  "dim.quarter": "Квартал",
  "dim.month": "Месяц",
  "dim.dataSource": "Источник данных",
  "dim.costType": "Тип затрат",
  "dim.counterpartyType": "Тип контрагента",
  "dim.account": "Категория",
  "dim.costCategory": "Категория затрат",
  "dim.revenueGroup": "Группа выручки",
  "dim.assetClass": "Класс активов",
  "dim.counterpartyName": "Контрагент",
  "dim.clientName": "Клиент",
  "dim.entity": "Юр. лицо",
  "dim.currency": "Валюта",
  "dim.contraCategory": "Корр. категория",
  "dim.serviceType": "Тип услуги",
  "data.AUM": "Управление портфелем",
  "data.AUC": "Хранение активов",
  "data.IA": "Инвестиционное консультирование",
  "data.OWN": "Собственная книга",

  // ── Default panel titles ──
  "panel.revenueByClient": "Выручка по клиентам (2025)",
  "panel.aumTrend": "AUM / AUC (помесячный тренд)",
  "panel.revenueVsExpenses": "Выручка vs Расходы (по годам)",
  "panel.expensesByCategory": "Расходы по категориям (по годам)",

  // ── Template folders ──
  "folder.revenue_pl": "Выручка и P&L",
  "folder.assets_aum": "Активы и AUM",
  "folder.clients": "Клиенты",
  "folder.costs": "Анализ затрат",
  "folder.custom": "Свои шаблоны",
  "folder.label": "Папки",
  "folder.folderLabel": "Папка",

  // ── Template browser ──
  "tpl.browseDescFull": "Просмотр шаблонов по категориям или загрузка сохранённой конфигурации в новое рабочее пространство.",
  "tpl.noCustom": "Своих шаблонов пока нет",
  "tpl.noTemplates": "В этой папке нет шаблонов",
  "tpl.noCustomHint": "Сохраните конфигурацию графика, чтобы создать шаблон",
  "tpl.builtin": "Встроенный",

  // ── Panel picker ──
  "picker.title": "Заменить панель",
  "picker.desc": "выберите панель для замены:",
  "picker.loading": "Загрузка",
  "picker.slot": "Слот",

  // ── Navigation ──
  "nav.portal": "Портал",
  "nav.backToPortal": "Вернуться в портал",

  // ── Group view toggle ──
  "entity.all": "Все",
  "view.residualTitle": "Вне обеих групп",
  "view.residualBody": "{rows} строк ({value}) отнесены к плательщику, которому ещё не назначена компания, поэтому они видны только в разделе «Все». WWG и The Vault в сумме не дают «Все».",
  "view.residualFix": "Назначьте их в разделе «Записи» → «Entity Names».",
  "view.residualExternal": "Ещё {rows} строк ({value}) помечены как внешние и исключены намеренно.",

  // ── KPI cards ──
  "kpi.totalRevenue": "Общая выручка",
  "kpi.totalExpenses": "Общие расходы",
  "kpi.operatingMargin": "Операционная маржа",
  "kpi.aumAuc": "AUM / AUC",
  "kpi.revenueMinusCosts": "Выручка минус расходы",
  "kpi.activeClients": "Активные клиенты",
  "kpi.activeClientsSub": "активных клиентов",
  "kpi.vs": "vs",
  "kpi.in": "в",

  // ── Default panel titles (WWDA) ──
  "panel.txVolByType": "Объём транзакций по типу",
  "panel.monthlyTxTrend": "Месячный тренд транзакций",
  "panel.topClientsByVol": "Топ клиентов по объёму",
  "panel.feeRevenueYearly": "Комиссионный доход (по годам)",
  "panel.balanceMonthlyTrend": "Баланс — месячный тренд",
  "panel.feeRevByCurrency": "Комиссионный доход по валюте",

  // ── Default panel titles (Unified) ──
  "panel.revByEntityYearly": "Выручка по юр. лицу (по годам)",
  "panel.topClientsAll": "Топ клиентов (все юр. лица)",
  "panel.monthlyTrendAll": "Месячный тренд (все юр. лица)",
  "panel.dataSourceBreakdown": "Разбивка по источникам данных",

  // ── Default panel titles (WWF) ──
  "panel.revVsExpenses": "Выручка vs Расходы",
  "panel.revByClient": "Выручка по клиентам",
  "panel.aumServiceMonthly": "AUM по типу услуги (помесячно)",
  "panel.costStructYearly": "Структура затрат (по годам)",

  // ── Builtin template names ──
  "panel.expRevByCategory": "Расходы и Выручка по категориям",
  "panel.clientRevVsCosts": "Выручка vs Расходы по клиентам",

  // ── 2026-04 pre-set rework (CFO spec) ──
  "panel.cashFlowWithNet": "Денежный поток — Выручка, Затраты и Чистая П/У",
  "panel.aumByClient": "AUM по клиентам (YTD)",
  "panel.revByMonthByClient": "Выручка по месяцам и клиентам (YTD)",
  "panel.costStructMonthly": "Структура затрат — помесячно",
  "panel.revByCounterpartyMonthly": "Выручка по месяцам и контрагентам (YTD)",
  "panel.revByCategoryMonthly": "Выручка по сервисным категориям — помесячно",
  "panel.treasuryBalanceMonthly": "Казначейский баланс по классам активов",
  "panel.feeRevMonthlyTrend": "Комиссионный доход — месячный тренд",
  "panel.walletBalanceByClient": "Баланс кошелька по клиентам (YTD)",
  "panel.feeRevByMonthByClient": "Комиссионный доход по месяцам и клиентам (YTD)",
  "panel.feeStructMonthly": "Структура комиссий — помесячно",

  // ── Dashboard presets ──
  "preset.menuLabel": "Готовые дашборды",
  "preset.default": "Стандартный",
  "preset.yearInReview": "Итоги года",
  "preset.topClients": "Топ-10 клиентов",
  "preset.payroll": "Аналитика ФОТ",
  "preset.costControl": "Контроль расходов и AP",
  "preset.byCompany": "По компаниям",
  "preset.planActual": "План и факт",
  "preset.wallet": "Кошелёк и транзакции",
  "preset.topClientsFees": "Топ-10 клиентов",

  // ── Preset panel titles ──
  "panel.p.revCostNetMonth": "Выручка · Расходы · Нетто — по месяцам (YTD)",
  "panel.p.netByQuarter": "Чистый денежный поток по кварталам (г/г)",
  "panel.p.costStructYtd": "Структура расходов по месяцам (YTD)",
  "panel.p.revByCategory": "Выручка по категориям (YTD)",
  "panel.p.top10ClientsRev": "Топ-10 клиентов по выручке (YTD)",
  "panel.p.top10ClientsTrend": "Топ-10 клиентов — динамика по месяцам (YTD)",
  "panel.p.clientConcentration": "Концентрация клиентов (YTD)",
  "panel.p.top10RevVsCost": "Топ-10 клиентов — выручка и расходы (YTD)",
  "panel.p.payrollMonthYoY": "ФОТ по месяцам — факт (г/г)",
  "panel.p.payrollByDivision": "ФОТ по дивизионам — план (YTD)",
  "panel.p.payrollByTeam": "ФОТ по командам — план (YTD)",
  "panel.p.payrollByPosition": "ФОТ по должностям — план (YTD)",
  "panel.p.topVendors": "Топ-15 поставщиков по расходам (YTD)",
  "panel.p.directVsOwn": "Прямые и собственные расходы — по месяцам (YTD)",
  "panel.p.apPipeline": "Этапы согласования счетов (YTD)",
  "panel.p.topCostCategories": "Крупнейшие категории расходов (YTD)",
  "panel.p.revCostByEntity": "Выручка и затраты по компаниям (YTD)",
  "panel.p.netByEntity": "Чистая прибыль по компаниям (YTD)",
  "panel.p.revByEntityMonth": "Выручка по компаниям — по месяцам (YTD)",
  "panel.p.payrollByEntity": "ФОТ по компаниям (YTD)",
  "panel.p.payrollEntityMonth": "ФОТ по компаниям — по месяцам (YTD)",
  "panel.p.byJurisdiction": "Выручка и затраты по юрисдикциям (YTD)",
  "panel.p.costByEntity": "Затраты по компаниям (YTD)",
  "panel.p.spendByEntity": "Расходы на поставщиков по компаниям (YTD)",
  "panel.p.planVsActualMonth": "План и факт — по месяцам (YTD)",
  "panel.p.planVsActualEntity": "План и факт — по компаниям (YTD)",
  "panel.p.planVsActualQuarter": "План и факт — по кварталам (YTD)",
  "panel.p.top10ClientsFees": "Топ-10 клиентов по комиссиям (YTD)",
  "panel.p.top10FeesTrend": "Топ-10 клиентов — комиссии по месяцам (YTD)",
  "panel.p.txVolumeMonth": "Объём транзакций по месяцам (г/г)",
  "panel.p.txByCategory": "Объём транзакций по операциям (YTD)",
  "panel.p.feeByType": "Комиссии по типам — по месяцам (YTD)",
  "panel.p.walletBalanceProduct": "Остатки кошельков по продуктам (YTD)",
  "panel.p.totalByMonth": "Итого по месяцам (YTD)",

  // ── NL Query ──
  "nl.placeholder": "Спросите что угодно... «выручка по клиентам за 2025»",
  "nl.query": "Запрос",
  "nl.chartCreated": "График создан",
  "nl.tryAsking": "Попробуйте спросить",
  "nl.metric": "Метрика",
  "nl.metricDefault": "(по умолчанию)",
  "nl.xaxis": "Ось X",
  "nl.split": "Разрез",
  "nl.chart": "График",
  "nl.yearFilter": "Фильтр по году",
  "nl.year": "Год",
  "nl.latest": "(последний)",
  "nl.sortDesc": "Сортировка: по убыванию (топ)",
  "nl.sortAsc": "Сортировка: по возрастанию (мин.)",
  "nl.ex1": "выручка по клиентам за 2025",
  "nl.ex2": "топ расходов по категориям за этот год",
  "nl.ex3": "помесячный тренд выручки по годам",
  "nl.ex4": "расходы vs выручка по годам столбчатая",
  "nl.ex5": "комиссионный доход по месяцам (область)",
  "nl.ex6": "концентрация клиентов — кто крупнейшие?",
  "nl.ex7": "объём транзакций по кварталам",
  "nl.ex8": "AUM по валюте круговая диаграмма",
  "nl.ex9": "сравнить выручку по юр. лицам как таблицу",

  // ── KPI (WWDA/ALL) ──
  "kpi.totalVolume": "Общий объём",
  "kpi.transactionVolume": "Объём транзакций",
  "kpi.feeRevenue": "Комиссионный доход",
  "kpi.aumBalance": "AUM / Баланс",
  "kpi.vsLastYear": "vs {value} в прошлом году",
  "kpi.unique": "уникальных",
  "kpi.lastYear": "в прошлом году",

  // ── Misc ──
  "misc.cancel": "Отмена",
  "misc.dims": "изм.",

  // ── Data value translations (chart legends, series, axis labels) ──
  // Flow types (value axis labels / chart legends)
  "data.Cash Flow": "Денежный поток",
  "data.Assets Held": "Активы",

  // Cost types
  "data.Direct": "Прямые",
  "data.Indirect": "Косвенные",
  "data.Regulatory": "Регуляторные",
  "data.Revenue": "Выручка",
  "data.Position": "Позиция",

  // Data sources
  "data.costs": "Затраты",
  "data.revenue": "Выручка",
  "data.net": "Чистая прибыль",
  "data.positions": "Позиции",
  "data.transactions": "Транзакции",
  "data.balances": "Остатки",
  "data.fees": "Комиссии",
  "data.jira_invoices": "Счета поставщиков",
  "data.payroll_actual": "ФОТ — детализация",
  "data.budget_expense": "Расходы (план)",
  "data.budget_payroll": "ФОТ (план)",
  "data.pl_plan": "P&L (план)",

  // Cost categories
  "data.Administration": "Администрирование",
  "data.Audit & Professional": "Аудит и проф. услуги",
  "data.Bank Charges": "Банковские комиссии",
  "data.Broker & Custodian Fees": "Брокерские и депозитарные",
  "data.Cost of Sales": "Себестоимость продаж",
  "data.Depreciation & Amortisation": "Амортизация",
  "data.IT & Infrastructure": "ИТ и инфраструктура",
  "data.Regulatory & Compliance": "Регулирование и комплаенс",
  "data.Tax & VAT": "Налоги и НДС",
  "data.Trading & Valuation Costs": "Торговые и оценочные расходы",

  // Revenue groups
  "data.Advisory Services": "Консультационные услуги",
  "data.Asset Management": "Управление активами",
  "data.Brokerage": "Брокерские услуги",
  "data.Interest & Dividends": "Проценты и дивиденды",
  "data.Other": "Прочее",
  "data.Trading & Revaluation": "Торговля и переоценка",

  // Revenue categories
  "data.Brokerage Commission": "Брокерская комиссия",
  "data.CSDR Fees": "Комиссии CSDR",
  "data.Corporate Action Fees": "Корп. действия",
  "data.Investment Advice": "Инвест. консультации",
  "data.Other Fees": "Прочие комиссии",
  "data.Other Income": "Прочие доходы",
  "data.Other Trading Income": "Прочие торговые доходы",
  "data.Revaluation": "Переоценка",
  "data.Safekeeping Fees": "Хранение ЦБ",
  "data.Trailer Fees": "Трейлерные комиссии",
  "data.Transfer Fees": "Комиссии за переводы",

  // Asset classes
  "data.Cash": "Денежные средства",
  "data.Securities": "Ценные бумаги",

  // Counterparty types
  "data.Client": "Клиент",
  "data.Own Account": "Собственный счёт",
  "data.Operational": "Операционный",
  "data.Vendor": "Поставщик",

  // Contra categories
  "data.Accruals": "Начисления",
  "data.Cash & Banks": "Денежные средства и банки",
  "data.Cost Reclassification": "Реклассификация затрат",
  "data.P&L Close": "Закрытие P&L",
  "data.Payables & Creditors": "Кредиторская задолженность",
  "data.Receivables & Investments": "Дебиторская задолженность и вложения",
  "data.Revenue Offset": "Корректировка выручки",

  // Entry sides
  "data.debit": "Дебет",
  "data.credit": "Кредит",
  "data.balance": "Баланс",

  // Months
  "data.Jan": "Янв", "data.Feb": "Фев", "data.Mar": "Мар",
  "data.Apr": "Апр", "data.May": "Май", "data.Jun": "Июн",
  "data.Jul": "Июл", "data.Aug": "Авг", "data.Sep": "Сен",
  "data.Oct": "Окт", "data.Nov": "Ноя", "data.Dec": "Дек",

  // Entity names are proper nouns and are not translated. They also no longer
  // need an entry here: the loader resolves every row to its registry name,
  // so "Wise Wolves Finance" and "Tria Wallet" are not values any more.
}

const translations: Record<Lang, Translations> = { en, ru }

/** Translation function factory */
export function getT(lang: Lang): (key: string) => string {
  const dict = translations[lang]
  const fallback = translations.en
  return (key: string) => dict[key] ?? fallback[key] ?? key
}

/**
 * Translate a data value (chart legend label, axis tick, series name).
 * Looks up "data.<value>" in translations. Returns original if no translation found.
 * Does NOT translate client names or counterparty names — those stay as-is.
 */
export function translateDataValue(t: (k: string) => string, value: string, _lang: Lang): string {
  // English goes through the same map. Several values are storage codes
  // rather than words — "jira_invoices", "budget_payroll", "AUM" — and they
  // need a display name in every language, not only in translated ones. The
  // map is display-only; nothing downstream compares against these strings.
  const key = `data.${value}`
  const result = t(key)
  // If t() returned the key itself (no translation found), return original value
  return result === key ? value : result
}

/** Dimension label map — maps dimension IDs to i18n keys */
export const DIM_I18N: Record<string, string> = {
  d_year: "dim.year",
  d_quarter: "dim.quarter",
  d_month: "dim.month",
  d_dataSource: "dim.dataSource",
  d_costType: "dim.costType",
  d_counterpartyType: "dim.counterpartyType",
  d_account: "dim.account",
  d_costCategory: "dim.costCategory",
  d_revenueGroup: "dim.revenueGroup",
  d_assetClass: "dim.assetClass",
  d_counterparty: "dim.counterpartyName",
  d_clientName: "dim.clientName",
  d_entity: "dim.entity",
  d_currency: "dim.currency",
  d_contraCategory: "dim.contraCategory",
  d_serviceType: "dim.serviceType",
}

/** Dimension group label map */
export const DIM_GROUP_I18N: Record<string, string> = {
  sg_time: "dim.timeline",
  sg_class: "dim.classification",
  sg_categories: "dim.categories",
  sg_counterparty: "dim.counterparty",
  sg_other: "dim.other",
}

/** Value item label map */
export const VALUE_I18N: Record<string, string> = {
  v_cashflow: "val.cashflow",
  v_assets: "val.assets",
  v_transactionvolume: "val.transactionvolume",
  v_balance: "val.balance",
  v_feerevenue: "val.feerevenue",
  grp_cashflow: "val.cashflow",
  grp_assets: "val.assets",
  grp_transactionvolume: "val.transactionvolume",
  grp_balance: "val.balance",
  grp_feerevenue: "val.feerevenue",
}
